import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import {
  Row,
  Col,
  Button,
  Dropdown,
  Menu,
  MenuProps,
  ConfigProvider,
} from 'antd';
import { MenuItemType } from 'antd/lib/menu/hooks/useItems';
import { DownOutlined, MenuOutlined } from '@ant-design/icons';

import useI18n from '../../common/hooks/useI18n';
import { IOffice } from '../../common/model/office.model';
import { SearchType } from '../../common/model/enums/searchType.enum';
import { FlatType } from '../../common/model/enums/flatType.enum';
import { removeCounty } from '../../common/helpers';
import { BuyHouseOption } from '../../common/helpers/buyHouseOption.utils';
import {
  OFFICES_URL_PREFIX,
  SEARCH_BY_COUNTY_SUFFIX,
  SELL_HOUSE_BLOG_URLS,
} from '../../common/consts';
import { getFlatTypeSlug } from '../../common/helpers/flatType.utils';
import { getOfficesByCountyPageSheetQueries } from '../../common/helpers/i18n.utils';
import { generateUrl } from '../../common/helpers/office.utils';
import {
  getTitleFromQuery,
  slugifySearchQuery,
} from '../../common/helpers/searchQuery.utils';

import HeaderDropdown from './headerDropdown';

type Props = {
  offices: IOffice[];
  buyHouseOptions: BuyHouseOption[];
  alwaysShown?: boolean;
  dropShadow?: boolean;
  alwaysShowSellHouseButton?: boolean;
};

const MenuButtons = styled(Row)`
  font-style: ${(props) => props.theme.font.style};
  font-weight: normal;
  font-size: 14px;
  line-height: 22px;
`;

const MdDownHiddenCol = styled(Col)<{ $alwaysShow?: boolean }>`
  @media ${(props) => props.theme.breakpoints.mdd} {
    display: ${(props) => (props.$alwaysShow ? 'block' : 'none')};
  }
`;

const LgUpHiddenCol = styled(Col)`
  @media ${(props) => props.theme.breakpoints.lgu} {
    display: none;
  }
`;

const HamburgerMenuContainer = styled.div`
  position: absolute;
  top: ${(props) => props.theme.headerHeight};
  left: 0;
  right: 0;

  .ant-menu-hidden {
    display: block;
    height: 0;
    opacity: 0;
  }

  .ant-menu-submenu:hover > .ant-menu-submenu-title > .ant-menu-submenu-arrow,
  .ant-menu-title-content > a {
    color: black;
  }

  @media ${(props) => props.theme.breakpoints.lgu} {
    display: none;
  }
`;

const HeaderComp = styled.header<{ alwaysShown: boolean; dropShadow: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1040;

  width: 100%;
  height: ${(props) => props.theme.headerHeight};

  padding-left: ${(props) => props.theme.grid.getGridColumns(1, 1)};
  padding-right: ${(props) => props.theme.grid.getGridColumns(1, 1)};
  @media ${(props) => props.theme.breakpoints.xs} {
    padding-left: 16px;
    padding-right: 8px;
  }
  @media ${(props) => props.theme.breakpoints.xxl} {
    padding-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
    padding-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  }

  background-color: ${(props) =>
    props.alwaysShown ? 'white' : 'rgba(0, 0, 0, 0)'};
  position: ${(props) => (props.alwaysShown ? 'inherit' : 'absolute')};
  @media ${(props) => props.theme.breakpoints.mdd} {
    background-color: white;
    position: inherit;
  }

  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${(props) =>
    props.dropShadow ? '0px 3px 25px rgba(0, 0, 0, 0.15)' : 'inherit'};

  .ant-menu-light .ant-menu-submenu-title:hover {
    color: rgba(0, 0, 0, 0.85);
  }
`;

const Logo = styled.div`
  position: relative;
  height: 32px;
  width: 130px;
  @media ${(props) => props.theme.breakpoints.xs} {
    height: 25.6px;
    width: 104px;
  }
`;

export const ButtonTextAnchor = styled.a<{ $primary?: boolean }>`
  color: ${(props) => (props.$primary ? 'inherit' : 'rgba(0, 0, 0, 0.85)')};
  border-color: transparent;
  background: transparent;
  box-shadow: none;

  &:hover {
    color: ${(props) => (props.$primary ? 'inherit' : 'rgba(0, 0, 0, 0.85)')};
    border-color: transparent;
    background: transparent;
    box-shadow: none;
  }
`;

const getItem = (
  label: React.ReactNode,
  key: React.Key,
  children?: MenuItemType[],
  direction?: string,
  type?: 'group'
): MenuItemType => {
  return {
    key,
    children,
    label,
    type,
    popupClassName: direction === 'rtl' ? 'rtl-dropdown' : undefined,
  } as MenuItemType;
};

const getSimpleChildItem = (
  href: string,
  text: string,
  key: React.Key
): MenuItemType =>
  getItem(
    <Link href={href} legacyBehavior>
      <a>
        <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
      </a>
    </Link>,
    key
  );

const getOfficeItem = (office: IOffice, offices: IOffice[]) => {
  return getItem(
    <Link href={generateUrl(office)} legacyBehavior>
      <a>
        <span>{office.name}</span>
      </a>
    </Link>,
    `office-${office.name}`
  );
};

const Header = ({
  offices,
  buyHouseOptions,
  alwaysShown,
  dropShadow,
  alwaysShowSellHouseButton,
}: Props): JSX.Element => {
  const router = useRouter();
  const i18n = useI18n();

  const hamburgerMenuRef = useRef(null);

  const [hamburgerMenuVisible, setHamburgerMenuVisible] = useState(false);

  const sellHouseOptions = useMemo(() => SELL_HOUSE_BLOG_URLS, []);

  const nonCapitalCities = useMemo(
    () =>
      getOfficesByCountyPageSheetQueries()
        .filter((q) => q.split('/').length > 1)
        .map((q) => q.substring(q.lastIndexOf('/') + 1)),
    []
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        hamburgerMenuRef.current &&
        !hamburgerMenuRef.current.contains(event.target)
      ) {
        setTimeout(() => setHamburgerMenuVisible(false));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setHamburgerMenuVisible(false);
  }, [router.asPath, setHamburgerMenuVisible]);

  const getSellHouseItems = (): MenuItemType[] | undefined => {
    if (sellHouseOptions.length === 0) {
      return undefined;
    }

    return sellHouseOptions.map((o) =>
      getSimpleChildItem(
        o.url,
        i18n.t(`header.sellHouse.${o.i18nKey}`),
        o.i18nKey
      )
    );
  };

  const getBuyHouseItems = (): MenuItemType[] => {
    return buyHouseOptions
      .slice(1)
      .map(({ flatType, query, text }) =>
        getSimpleChildItem(
          `/comprar/${getFlatTypeSlug(flatType, true)}/${query}`,
          getTitleFromQuery(
            removeCounty(text),
            SearchType.CLOSED,
            FlatType.PISO,
            i18n
          ),
          query
        )
      );
  };

  const getOfficesItems = (direction?: string): MenuItemType[] => {
    return [
      getItem(
        i18n.t('header.offices.provinces'),
        'provinces',
        Array.from(new Set(offices.map((office) => office.province)).values())
          .map((province) => {
            const officesByProvince = offices.filter(
              (office) => office.province === province
            );
            return { province, officesByProvince };
          })
          .sort(
            (a, b) => b.officesByProvince.length - a.officesByProvince.length
          )
          .map(({ province, officesByProvince }) => {
            const officesByProvinceItems = [
              getItem(
                i18n.t('header.offices.citiesOf', { province }),
                `cities-${province}`,
                Array.from(
                  new Set(
                    officesByProvince.map((office) => office.city)
                  ).values()
                )
                  .map((city) => {
                    const officesByCity = offices.filter(
                      (office) => office.city === city
                    );
                    return { city, officesByCity };
                  })
                  .sort(
                    (a, b) => b.officesByCity.length - a.officesByCity.length
                  )
                  .map(({ city, officesByCity }) => {
                    if (officesByCity.length === 1) {
                      return getOfficeItem(officesByCity[0], offices);
                    }

                    officesByCity.sort((a, b) => a.name.localeCompare(b.name));
                    const officesByCityItems = [
                      getItem(
                        i18n.t('header.offices.officesInCity', { city }),
                        `offices-${city}`,
                        officesByCity.map((office) =>
                          getOfficeItem(office, offices)
                        ),
                        direction,
                        'group'
                      ),
                    ];

                    const citySpan = (
                      <span
                        style={{ whiteSpace: 'nowrap' }}
                      >{`${city} (${officesByCity.length})`}</span>
                    );
                    return getItem(
                      nonCapitalCities.findIndex((cq) =>
                        cq.includes(slugifySearchQuery(city))
                      ) >= 0 ? (
                        <Link
                          href={`/${OFFICES_URL_PREFIX}/${
                            slugifySearchQuery(province) +
                            SEARCH_BY_COUNTY_SUFFIX
                          }/${slugifySearchQuery(city)}`}
                          legacyBehavior
                        >
                          <a>{citySpan}</a>
                        </Link>
                      ) : (
                        citySpan
                      ),
                      `city-${city}`,
                      officesByCityItems,
                      direction
                    );
                  }),
                direction,
                'group'
              ),
            ];

            return getItem(
              <Link
                href={`/${OFFICES_URL_PREFIX}/${
                  slugifySearchQuery(province) + SEARCH_BY_COUNTY_SUFFIX
                }`}
                legacyBehavior
              >
                <a>
                  <span
                    style={{ whiteSpace: 'nowrap' }}
                  >{`${province} (${officesByProvince.length})`}</span>
                </a>
              </Link>,
              `province-${province}`,
              officesByProvinceItems,
              direction
            );
          }),
        direction,
        'group'
      ),
    ];
  };

  const sellHouseItems = getSellHouseItems();

  const items: MenuProps['items'] = [
    !alwaysShowSellHouseButton &&
      getItem(
        <Link href="/vender-casa" legacyBehavior>
          <ButtonTextAnchor as="a">
            {i18n.t('header.sellHouse.title')}
          </ButtonTextAnchor>
        </Link>,
        'sellHouse',
        sellHouseItems
      ),
    getItem(
      <Link href="/comprar" legacyBehavior>
        <ButtonTextAnchor as="a">{i18n.t('header.buyHouse')}</ButtonTextAnchor>
      </Link>,
      'buyHouse',
      getBuyHouseItems()
    ),
    getItem(
      <Link href={`/${OFFICES_URL_PREFIX}`} legacyBehavior>
        <ButtonTextAnchor as="a">
          {i18n.t('header.offices.title')}
        </ButtonTextAnchor>
      </Link>,
      'offices',
      getOfficesItems()
    ),
    getItem(
      <Link href="/nucleo" legacyBehavior>
        <a>{i18n.t('header.aboutUs')}</a>
      </Link>,
      'aboutUs'
    ),
    getItem(
      <Link href="/contacto" legacyBehavior>
        <a>{i18n.t('header.contact')}</a>
      </Link>,
      'contact'
    ),
    getItem(
      <Link href="/blog" legacyBehavior>
        <a>{i18n.t('header.blog')}</a>
      </Link>,
      'blog'
    ),
  ];

  return (
    <HeaderComp
      as="header"
      alwaysShown={!!alwaysShown}
      dropShadow={!!dropShadow}
    >
      <Link href="/" legacyBehavior>
        <a>
          <Logo>
            <Image
              alt={i18n.t('header.logoAlt')}
              layout="fill"
              src="/images/LogoHeaderWeb.svg"
            />
          </Logo>
        </a>
      </Link>
      <MenuButtons gutter={16} align="middle">
        {sellHouseItems ? (
          <MdDownHiddenCol
            id="sell-house-container"
            $alwaysShow={alwaysShowSellHouseButton}
          >
            <HeaderDropdown
              items={sellHouseItems}
              containerId="sell-house-container"
              buttonHref="/vender-casa"
              buttonTextKey="header.sellHouse.title"
              buttonPrimary
            />
          </MdDownHiddenCol>
        ) : (
          <MdDownHiddenCol $alwaysShow={alwaysShowSellHouseButton}>
            <Link href="/vender-casa" passHref>
              <Button type="primary">{i18n.t('header.sellHouse.title')}</Button>
            </Link>
          </MdDownHiddenCol>
        )}
        <MdDownHiddenCol id="buy-house-container">
          <HeaderDropdown
            items={getBuyHouseItems()}
            containerId="buy-house-container"
            buttonHref="/comprar"
            buttonTextKey="header.buyHouse"
          />
        </MdDownHiddenCol>
        <MdDownHiddenCol id="offices-container">
          <ConfigProvider>
            <Dropdown
              overlay={
                <ConfigProvider direction="rtl">
                  <Menu items={getOfficesItems('rtl')} />
                </ConfigProvider>
              }
              overlayClassName="no-opacity-dropdown rtl-dropdown"
              getPopupContainer={() =>
                document.getElementById('offices-container')
              }
            >
              <Button type="text" onClick={(e) => e.preventDefault()}>
                <span>
                  <Link href={`/${OFFICES_URL_PREFIX}`} passHref legacyBehavior>
                    <ButtonTextAnchor as="a" className="ant-dropdown-trigger">
                      {i18n.t('header.offices.title')}
                    </ButtonTextAnchor>
                  </Link>
                </span>
                <DownOutlined />
              </Button>
            </Dropdown>
          </ConfigProvider>
        </MdDownHiddenCol>
        <MdDownHiddenCol>
          <Link href="/nucleo" passHref>
            <Button type="text">{i18n.t('header.aboutUs')}</Button>
          </Link>
        </MdDownHiddenCol>
        <MdDownHiddenCol>
          <Link href="/contacto" passHref>
            <Button type="text">{i18n.t('header.contact')}</Button>
          </Link>
        </MdDownHiddenCol>
        <MdDownHiddenCol>
          <Link href="/blog" passHref>
            <Button type="text">{i18n.t('header.blog')}</Button>
          </Link>
        </MdDownHiddenCol>
        <LgUpHiddenCol>
          <Button
            type="text"
            shape="circle"
            icon={<MenuOutlined />}
            onClick={() => {
              if (!hamburgerMenuVisible) {
                setHamburgerMenuVisible(true);
              }
            }}
          ></Button>
        </LgUpHiddenCol>
      </MenuButtons>
      {hamburgerMenuVisible && (
        <HamburgerMenuContainer as="div" ref={hamburgerMenuRef}>
          <Menu mode="inline" items={items} />
        </HamburgerMenuContainer>
      )}
    </HeaderComp>
  );
};

export default Header;
